import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { erpApi } from '@/shared/axios/erp-api';
import {
  ServicesType,
  ServicesTypeDataPagingRes,
} from '@/shared/interfaces/erp/system-settings';
import { useUiStore } from '@/store/ui';
import { urlBackend } from '@/shared/constants/networking.constants';
import { getUrlParamsFetch } from '@/shared';

const { get, post, put, remove } = erpApi();

///* tanSctack query
export const useFetchServicesType = ({
  page = 1,
  nombre = '',
  page_size = 10,
}: getServicesTypeParams) => {
  return useQuery({
    queryKey: ['servicesType', page, nombre],
    queryFn: async () => {
      return getServicesType({ page, nombre, page_size });
    },
  });
};

export const useGetServiceType = (id: string) => {
  return useQuery({
    queryKey: ['servicesType', id],
    queryFn: async () => {
      return getServiceType(id);
    },
  });
};

export const useCreateServiceType = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createServiceType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servicesType'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

export const useUpdateServiceType = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateServiceType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servicesType'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

export const useRemoveServiceType = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: removeServiceType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servicesType'] });
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
    },
  });
};

///* axios
export type getServicesTypeParams = {
  page?: number;
  nombre?: string;
  page_size?: number;
};
export type CreateServiceTypeParams = Omit<ServicesType, 'id_tipo_servicio'>;
export type UpdateServiceTypeParams = {
  id: string;
  data: CreateServiceTypeParams;
};

export const getServicesType = async (params?: getServicesTypeParams) => {
  const queryParams = getUrlParamsFetch({ ...params });
  return get<ServicesTypeDataPagingRes>(
    `${urlBackend}api/v1/tipos-servicios/?${queryParams}`,
    true
  );
};

export const getServiceType = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<ServicesType>(
    `${urlBackend}api/v1/tipos-servicios/${id}`,
    true
  );

  res.status === 'error' && setAlert(true, res.message, res.status);
  return res;
};

export const createServiceType = async (data: CreateServiceTypeParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<ServicesType>(
    `${urlBackend}api/v1/tipos-servicios/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updateServiceType = async ({
  id,
  data,
}: UpdateServiceTypeParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<ServicesType>(
    `${urlBackend}api/v1/tipos-servicios/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const removeServiceType = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<ServicesType>(
    `${urlBackend}api/v1/tipos-servicios/${id}/`,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
